import React, { useState } from 'react';
import Button from '@app/react-common/components/button/Button';
import Dropdown, { Option } from '@app/react-common/components/dropdown/Dropdown';
import Input from '@app/react-common/components/input/Input';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';

const FIELD_OPTIONS: Option[] = [{ label: 'Variant Metafield', value: 'variantMetafield' }];

const VARIANT_METAFIELD_FIELD = 'variantMetafield';

type GroupByWithFallback = Checkout.CategoryShippingGroupBy & {
  productMetafieldFallback?: { namespace: string; key: string };
};

interface GroupByControlProps {
  isGrouped: boolean;
  groupBy?: Checkout.CategoryShippingGroupBy;
  onApply: (groupBy: Checkout.CategoryShippingGroupBy) => void;
  onClear: () => void;
}

export const GroupByControl = ({ isGrouped, groupBy, onApply, onClear }: GroupByControlProps) => {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState<Option | null>(null);
  const [metafieldKey, setMetafieldKey] = useState<string>('');
  const [productFallbackKey, setProductFallbackKey] = useState<string>('');

  const openPopover = () => {
    if (isGrouped && groupBy) {
      // Already configured -> show the current field + key immediately.
      setField(FIELD_OPTIONS.find((option) => option.value === groupBy.field) ?? null);
      const namespace = groupBy.metafield?.namespace;
      const key = groupBy.metafield?.key;
      setMetafieldKey(namespace && key ? `${namespace}.${key}` : (key ?? ''));
      const fallback = (groupBy as GroupByWithFallback).productMetafieldFallback;
      setProductFallbackKey(
        fallback?.namespace && fallback?.key ? `${fallback.namespace}.${fallback.key}` : (fallback?.key ?? ''),
      );
    } else {
      // Nothing configured (or just cleared) -> start fresh.
      setField(null);
      setMetafieldKey('');
      setProductFallbackKey('');
    }
    setOpen(true);
  };

  const clear = () => {
    onClear();
    setField(null);
    setMetafieldKey('');
    setProductFallbackKey('');
    setOpen(false);
  };

  const apply = () => {
    if (!field || !metafieldKey.trim()) {
      return;
    }
    const trimmed = metafieldKey.trim();
    const dotIndex = trimmed.indexOf('.');
    const namespace = dotIndex === -1 ? '' : trimmed.slice(0, dotIndex).trim();
    const key = dotIndex === -1 ? trimmed : trimmed.slice(dotIndex + 1).trim();
    if (!key) {
      return;
    }
    const nextGroupBy: GroupByWithFallback = {
      field: field.value as Checkout.CategoryGroupByFieldType,
      metafield: { namespace, key },
    };
    const fallbackRaw = productFallbackKey.trim();
    if (fallbackRaw) {
      const fallbackDotIndex = fallbackRaw.indexOf('.');
      const fallbackNamespace = fallbackDotIndex === -1 ? '' : fallbackRaw.slice(0, fallbackDotIndex).trim();
      const fallbackKey = fallbackDotIndex === -1 ? fallbackRaw : fallbackRaw.slice(fallbackDotIndex + 1).trim();
      if (fallbackKey) {
        nextGroupBy.productMetafieldFallback = { namespace: fallbackNamespace, key: fallbackKey };
      }
    }
    onApply(nextGroupBy);
    setOpen(false);
  };

  const isVariantMetafield = field?.value === VARIANT_METAFIELD_FIELD;

  return (
    <div className="delivery-methods-list__group-by">
      <Button
        text="Group By"
        icon="groups"
        iconPosition="left"
        onClick={() => (open ? setOpen(false) : openPopover())}
        size="medium"
        variant={isGrouped ? 'primary' : 'secondary'}
      />
      {open && (
        <div className="delivery-methods-list__group-by__popover">
          <Dropdown
            options={FIELD_OPTIONS}
            placeholder="Select field"
            preSelectedOption={field}
            onSelect={(option) => setField(option)}
            position="below"
          />
          {field && (
            <>
              <Input
                type="text"
                value={metafieldKey}
                onChange={(e) => setMetafieldKey(e.target.value)}
                placeholder="Please type the Metafield key"
              />
              {isVariantMetafield && (
                <Input
                  type="text"
                  value={productFallbackKey}
                  onChange={(e) => setProductFallbackKey(e.target.value)}
                  placeholder="Product metafield fallback key (optional)"
                />
              )}
              <div className="delivery-methods-list__group-by__actions">
                <Button text="Clear" onClick={clear} size="medium" variant="secondary" />
                <Button text="Apply" onClick={apply} size="medium" variant="primary" disabled={!metafieldKey.trim()} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
